import { captureException } from "@storytell/analytics";
import { GiftIcon, InformationIcon } from "@storytell/bsky-icons";
import { dispatchGlobalEvent, isNetworkError } from "@storytell/events";
import { TbCheck, TbCopy } from "solid-icons/tb";
import {
  type Component,
  createSignal,
  type JSX,
  type ParentComponent,
  Show,
  ErrorBoundary as SolidErrorBoundary,
} from "solid-js";
import { useSafeI18n } from "~/i18n/context";
import { DynamicImportError } from "~/lib/lazyWithReload";

interface ErrorBoundaryProps {
  fallback?: (error: Error, reset: () => void) => JSX.Element;
}

const reloadPage = () => {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
};

// Stale chunk errors occur when a deploy replaces hashed asset files while a tab is still open;
// lazy-loaded routes then fail because the old hash no longer exists on the CDN.
//
// Our own lazy imports are wrapped with `lazyWithReload` (see ~/lib/lazyWithReload), so the
// rejected import is tagged as DynamicImportError and detected without string matching.
// These patterns are a fallback for imports we don't control (e.g., chunk loaders the
// framework wires up internally). Browser phrasings can drift across engine versions, so
// keep this list narrow and treat misses as acceptable — the fallback UI still recovers.
const STALE_CHUNK_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Failed to load module script/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

const isStaleChunkError = (error: Error): boolean => {
  if (error instanceof DynamicImportError) return true;
  const haystack = `${error.name}: ${error.message}`;
  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(haystack));
};

// A stale chunk is gone for good, so the only recovery is to reload and let the browser pick up
// the current HTML (with its current chunk hashes). The cooldown isn't there to wait for the chunk
// to come back — it's there so we don't reload in a tight loop if, on the next load, the page somehow
// still points at a dead chunk hash (bfcache, HTTP memory cache, or a misbehaving intermediary cache).
// A long-open tab that hits a new stale chunk hours later is outside the window, so it still auto-reloads.
const STALE_CHUNK_RELOAD_KEY = "stale-chunk-reload-at";
const STALE_CHUNK_RELOAD_COOLDOWN_MS = 10_000;

const attemptStaleChunkReload = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < STALE_CHUNK_RELOAD_COOLDOWN_MS) {
      return false;
    }
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (restricted iframe, site data blocked, etc.) — bail out.
    // Without a place to record the attempt we can't cap reloads, so fall through to the manual-recovery UI.
    return false;
  }
  window.location.reload();
  return true;
};

// Limits error details to 1500 chars to stay within safe mailto URL length limits (~2000-3000 chars total)
const buildSupportMailtoUrl = (error: Error): string => {
  const subject = "Help - I got an unexpected error on Chief";

  let errorDetails = `${error.name}: ${error.message}`;
  if (error.stack) {
    errorDetails += `\n\n${error.stack}`;
  }

  // Truncate if too long (1500 chars before encoding to account for URL encoding expansion)
  const MAX_ERROR_LENGTH = 1500;
  if (errorDetails.length > MAX_ERROR_LENGTH) {
    errorDetails = `${errorDetails.substring(0, MAX_ERROR_LENGTH)}\n\n[... error details truncated ...]`;
  }

  const body = `Help, I got this unexpected error on Storytell.
Please add 1,000 credits into my account for reporting this error.
And for 4,000 more credits, here's one thing I'd like to see you do to improve Storytell: 
=================
Tell us here!

=================

${errorDetails}`;

  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  return `mailto:Support@Chief.bot?subject=${encodedSubject}&body=${encodedBody}`;
};

/** Renders outside `I18nProvider` when the root boundary catches — must use `useSafeI18n`, not `useI18n`. */
const DefaultErrorFallbackView: Component<{ error: Error }> = (props) => {
  const { t } = useSafeI18n();
  const errorDetailsLabel = t("error_details") === "error_details" ? "Error details" : t("error_details");
  const [copied, setCopied] = createSignal(false);

  const errorText = () => {
    const base = `${props.error.name}: ${props.error.message}`;
    return props.error.stack ? `${base}\n\n${props.error.stack}` : base;
  };

  const copyErrorDetails = async () => {
    try {
      await navigator.clipboard.writeText(errorText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy error details:", err);
    }
  };

  return (
    <div class="flex min-h-screen items-center justify-center p-4">
      <div class="w-full max-w-2xl rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900/40">
        <h3 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Unexpected Error - Let's Fix It</h3>
        <p class="mb-4 text-gray-700 dark:text-gray-300">
          This wasn't your fault. Your browser tab may be out of date; reloading should help.
        </p>
        <details class="mb-4">
          <summary class="mb-2 cursor-pointer text-gray-700 dark:text-gray-300">{errorDetailsLabel}</summary>
          <div class="relative">
            <button
              type="button"
              onClick={() => void copyErrorDetails()}
              aria-label={copied() ? "Copied" : "Copy error details"}
              title={copied() ? "Copied" : "Copy error details"}
              class="absolute top-2 right-2 rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/60 dark:hover:text-gray-200"
            >
              <Show when={copied()} fallback={<TbCopy class="size-4" aria-hidden="true" />}>
                <TbCheck class="size-4 text-green-600 dark:text-green-400" aria-hidden="true" />
              </Show>
            </button>
            <pre class="overflow-auto rounded bg-gray-100 p-3 pr-10 text-sm text-gray-600 dark:bg-gray-800/40 dark:text-gray-400">
              {props.error.name}: {props.error.message}
              <Show when={props.error.stack}>
                <>
                  {"\n\n"}
                  {props.error.stack}
                </>
              </Show>
            </pre>
          </div>
        </details>
        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <button
              type="button"
              onClick={reloadPage}
              class="rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
            >
              Reload Page
            </button>
          </div>
          <div class="flex items-start gap-2 text-gray-700 dark:text-gray-300">
            <GiftIcon class="mt-0.5 size-5 shrink-0 text-amber-500 dark:text-amber-400" aria-hidden="true" />
            <p>
              If you{" "}
              <a
                href={buildSupportMailtoUrl(props.error)}
                class="font-bold text-blue-600 hover:underline dark:text-blue-400"
              >
                email us to report the error
              </a>
              , we'll thank you by adding <strong>1,000</strong> extra credits into your account. If you also tell us
              how you'd like us to improve Storytell, we'll load <strong>5,000</strong> extra credits into your account!
            </p>
          </div>
        </div>
        <div class="mt-4 flex justify-end">
          <a
            href="https://go.chief.bot/learn-report-errors"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-sm text-brand underline"
          >
            <InformationIcon class="size-4 shrink-0" aria-hidden="true" />
            Learn more
          </a>
        </div>
      </div>
    </div>
  );
};

const defaultErrorFallback = (_error: Error, _reset: () => void) => <DefaultErrorFallbackView error={_error} />;

export const ErrorBoundary: ParentComponent<ErrorBoundaryProps> = (props) => {
  return (
    <SolidErrorBoundary
      fallback={(error: Error, reset: () => void) => {
        const staleChunk = isStaleChunkError(error);
        // The client hands back fetch's own TypeError, and resources rethrow it into render. Its
        // HTTP errors are worded "Failed to fetch: …" too, but are plain Errors and stay reported.
        if (!staleChunk && error instanceof TypeError && isNetworkError(error)) {
          dispatchGlobalEvent("connectivity-api-error", { error: String(error) });
        } else {
          captureException(error, { errorType: staleChunk ? "StaleChunkReload" : "ErrorBoundary" });
        }
        console.error("ErrorBoundary caught error:", error);

        if (staleChunk && attemptStaleChunkReload()) {
          // Reload is in-flight — render nothing to avoid flashing the error UI.
          return <></>;
        }

        if (props.fallback) {
          return props.fallback(error, reset);
        }

        return defaultErrorFallback(error, reset);
      }}
    >
      {props.children}
    </SolidErrorBoundary>
  );
};
